import { Observable, forkJoin, of, throwError } from "rxjs";
import { delay } from "rxjs/operators";
import {
  Product,
  ProductType,
  ProductStatus,
  ProductMedia,
  ProductCategory,
  ItineraryStop,
  ProductFeature,
  ProductSession,
  CancellationPolicy,
  ProductAddon,
  SchedulePattern,
  FeatureType,
  MeetingPickUpSetting,
  PickupPointCategory,
  MeetingPoint,
  ResellerType,
  AdditionalInfoTypeEnum,
  PriceUnit,
  GroupDefinitionEnum,
  ProductSeason,
  PricingRecord,
  ProductOption,
  AgeDefinition,
  AgeBand,
  Operator,
  OperatorStatusEnum,
  Affiliate,
  AffiliateStatusEnum,
  AffiliateSiteConfig,
  CompletionStageEnum,
  UpdateOperatorProfileDto,
} from "./tour.models";
import { Injectable, NgZone } from "@angular/core";
import { environment } from "src/environments/environment";
import { from } from "rxjs";
import { map, mapTo, shareReplay, switchMap, tap } from "rxjs/operators";
import { HttpRequestsService } from "src/app/services/http-requests.service";
import { ProductMapper } from "./product.mapper";
import { allStepIdsOrdered } from "./product-creation/production-creation.component";
import {
  FlexibleDurationUnit,
  ProductCreationStage,
  ProductService,
} from "./product.service";
import { UpdatePricingSettingsDto } from "./dto/booking-setting.dto";
import { flow } from "ramda";
import { formDataTrait } from "src/app/utils/form-data";

@Injectable()
export class ProductServiceIplm extends ProductService {
  private tourServiceHost: string = environment.TOUR_SERVICE;
  private operator: Operator;
  private affiliate: Affiliate;
  private operatorRequest$: Observable<Operator> | null = null;
  private affiliateRequest$: Observable<Affiliate> | null = null;
  private lastInitTime: number = 0;

  constructor(
    private http: HttpRequestsService,
    ngZone: NgZone,
  ) {
    super(ngZone);
    http.setAutoReportError(false);
  }

  public override clearState(): void {
    super.clearState();
    this.operator = null;
    this.affiliate = null;
    this.operatorRequest$ = null;
    this.affiliateRequest$ = null;
    this.lastInitTime = 0;
  }

  public ensureInitialized(): void {
    const now = Date.now();
    if (now - this.lastInitTime < 500) {
      return;
    }

    if (!this.operatorRequest$ && !this.operator) {
      this.lastInitTime = now;
      this.initOperatorStatus();
    }

    if (!this.affiliateRequest$ && !this.affiliate) {
      this.lastInitTime = now;
      this.initAffiliateStatus();
    }
  }

  private initOperatorStatus() {
    this.initialLoadingSubject.next(true);
    this.getOperator(false).subscribe({
      next: (operator) => {
        this.ngZone.run(() => {
          const isActiveOperator =
            operator?.status === OperatorStatusEnum.ACTIVE;
          this.operatorSubject.next(operator);
          this.isOperatorSubject.next(isActiveOperator);
          this.initialLoadingSubject.next(false);
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          this.operatorSubject.next(null);
          this.isOperatorSubject.next(false);
          this.initialLoadingSubject.next(false);
        });
      },
    });
  }

  private initAffiliateStatus() {
    this.getAffiliate(false).subscribe({
      next: (affiliate) => {
        this.ngZone.run(() => {
          this.affiliateSubject.next(affiliate);
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          this.affiliateSubject.next(null);
        });
      },
    });
  }

  getOperator(isReload: boolean): Observable<Operator> {
    if (isReload) {
      this.operator = null;
      this.operatorRequest$ = null;
    }

    if (this.operator) {
      return from(Promise.resolve(this.operator));
    }

    if (this.operatorRequest$) {
      return this.operatorRequest$;
    }

    this.operatorRequest$ = from(
      this.http.rawGet(
        this.tourServiceHost,
        "/operators/me",
        false,
        {},
        {},
        false,
      ),
    ).pipe(
      map((res) => res.data),
      tap({
        next: (response: any) => {
          this.operator = response;
          this.operatorSubject.next(response);
        },
        error: (error) => {
          this.operator = null;
          this.operatorRequest$ = null;
          this.operatorSubject.next(null);
          this.isOperatorSubject.next(false);
        },
      }),
      shareReplay(1),
    );

    return this.operatorRequest$;
  }

  checkIfOperator(recheck = false): Observable<boolean> {
    return this.getOperator(recheck).pipe(
      map((operator) => {
        this.operatorSubject.next(operator);
        const isOp = !!operator;
        return isOp;
      }),
    );
  }

  checkIfActiveOperator(recheck = false): Observable<boolean> {
    return this.getOperator(recheck).pipe(
      map((operator) => {
        this.operatorSubject.next(operator);
        const isActiveOp = operator?.status === OperatorStatusEnum.ACTIVE;
        this.isOperatorSubject.next(isActiveOp);
        return isActiveOp;
      }),
    );
  }

  becomeTourOperator(profile: any): Observable<Operator> {
    const data = flow(new FormData(), [
      formDataTrait.guardedSet("name", profile.name),
      formDataTrait.guardedSet("description", profile.description),
      formDataTrait.guardedSet("website", profile.website),
      formDataTrait.guardedSet("logo", profile.logo),
    ]);
    return from(
      this.http.rawPost<{ data: Operator }>(
        this.tourServiceHost,
        "/operators",
        data,
        false,
        {},
        false,
      ),
    ).pipe(
      switchMap(({ data }) => {
        this.operatorSubject.next(data);
        this.isOperatorSubject.next(false);
        return this.checkIfOperator(true).pipe(mapTo(data));
      }),
    );
  }

  getAffiliate(isReload: boolean): Observable<Affiliate> {
    if (isReload) {
      this.affiliate = null;
      this.affiliateRequest$ = null;
    }

    if (this.affiliate) {
      return from(Promise.resolve(this.affiliate));
    }

    if (this.affiliateRequest$) {
      return this.affiliateRequest$;
    }

    this.affiliateRequest$ = from(
      this.http.rawGet(
        this.tourServiceHost,
        "/affiliates/me",
        false,
        {},
        {},
        false,
      ),
    ).pipe(
      map((res) => {
        const affiliate = res.data;
        if (affiliate && !affiliate.status) {
          affiliate.status = AffiliateStatusEnum.ACTIVE;
        }
        return affiliate;
      }),
      tap({
        next: (response: any) => {
          this.affiliate = response;
          this.affiliateSubject.next(response);
        },
        error: (error) => {
          this.affiliate = null;
          this.affiliateRequest$ = null;
          this.affiliateSubject.next(null);
        },
      }),
      shareReplay(1),
    );

    return this.affiliateRequest$;
  }

  checkIfAffiliate(recheck = false): Observable<boolean> {
    return this.getAffiliate(recheck).pipe(
      map((affiliate) => {
        this.affiliateSubject.next(affiliate);
        return !!affiliate;
      }),
    );
  }

  checkIfActiveAffiliate(recheck = false): Observable<boolean> {
    return this.getAffiliate(recheck).pipe(
      map((affiliate) => {
        this.affiliateSubject.next(affiliate);
        return !!affiliate;
      }),
    );
  }

  becomeAffiliate(profile: any): Observable<Affiliate> {
    const data = {
      name: profile.name,
      location: profile.location,
    };
    return from(
      this.http.rawPost<{ data: Affiliate; message: string }>(
        this.tourServiceHost,
        "/affiliates/register",
        data,
        false,
      ),
    ).pipe(
      switchMap(({ data }) => {
        this.affiliateSubject.next(data);
        return this.checkIfAffiliate(true).pipe(mapTo(data));
      }),
    );
  }

  getAffiliateStats(): Observable<any> {
    return from(
      this.http.rawGet(this.tourServiceHost, "/affiliates/me/stats", false),
    ).pipe(map((res) => res.data));
  }

  getAffiliateBookings(): Observable<any> {
    return from(
      this.http.rawGet(this.tourServiceHost, "/affiliates/bookings", false),
    ).pipe(map((res) => res.data));
  }

  generateAffiliateLoginCode(): Observable<{ code: string }> {
    return from(
      this.http.rawGet<{ data: string; message: string }>(
        environment.apiEndPoint,
        "user/affiliate-login-code",
        false,
      ),
    ).pipe(map((res) => ({ code: res.data })));
  }

  getAffiliateBookingDetails(bookingId: string): Observable<any> {
    return from(
      this.http.rawGet(
        this.tourServiceHost,
        `/affiliates/bookings/${bookingId}`,
        false,
      ),
    ).pipe(map((res) => res.data));
  }

  cancelAffiliateBooking(
    bookingId: string,
    cancellationReason: string,
  ): Observable<any> {
    return from(
      this.http.rawPatch(
        this.tourServiceHost,
        `/affiliates/bookings/${bookingId}/cancel`,
        { cancellationReason },
        false,
      ),
    ).pipe(map((res) => res.data));
  }

  getAffiliatePayouts(page = 1, limit = 20): Observable<any> {
    return from(
      this.http.rawGet(
        this.tourServiceHost,
        `/affiliates/payouts?page=${page}&limit=${limit}`,
        false,
      ),
    ).pipe(map((res) => res.data));
  }

  getAffiliatePayoutDetails(payoutId: string): Observable<any> {
    return from(
      this.http.rawGet(
        this.tourServiceHost,
        `/affiliates/payouts/${payoutId}`,
        false,
      ),
    ).pipe(map((res) => res.data));
  }

  getAffiliateSiteConfig(): Observable<any> {
    return from(
      this.http.rawGet(this.tourServiceHost, "/affiliate-site", false),
    ).pipe(map((res) => res.data));
  }

  createAffiliateSiteConfig(formData: FormData): Observable<any> {
    return from(
      this.http.rawPost<{ data: AffiliateSiteConfig }>(
        this.tourServiceHost,
        "/affiliate-site",
        formData,
      ),
    ).pipe(map((res) => res.data));
  }

  updateAffiliateSiteConfig(formData: FormData): Observable<any> {
    return from(
      this.http.rawPut<{ data: AffiliateSiteConfig }>(
        this.tourServiceHost,
        "/affiliate-site",
        formData,
      ),
    ).pipe(map((res) => res.data));
  }

  submitAffiliateSiteForApproval(): Observable<any> {
    return from(
      this.http.rawPost<{ data: AffiliateSiteConfig }>(
        this.tourServiceHost,
        "/affiliate-site/submit",
        {},
      ),
    ).pipe(map((res) => res.data));
  }

  isTour(product: Product): boolean {
    return product.productType === ProductType.TOUR;
  }

  isActivity(product: Product): boolean {
    return product.productType === ProductType.ACTIVITY;
  }

  getCreationStage(product: Product): ProductCreationStage {
    return {
      basicDetails: !!product.title,
      productType: !!product.productType,
      categories: product.categories.length > 0,
      media: product.media.length > 0,
      meetingPoints: !!product.meetingPickupSetting,
      tourOrActivityDetails: this.isTour(product)
        ? !!product.tourDetails
        : !!product.activityDetails,
      description: !!product.description,
      accessibility:
        !!product.features &&
        product.features.some((f) => f.featureType === FeatureType.INCLUSION),
      pricing: !!product.bookingSettings?.priceUnit,
      scheduling: false,
      bookingSettings: !!product.bookingSettings,
    };
  }

  createProductDraft(
    title: string,
    language: string,
    productType: ProductType,
    productClassification?: string,
  ): Observable<Product> {
    const url = `/operators/products`;
    const body: any = {
      title,
      language,
      productType,
      completionStage: allStepIdsOrdered[0],
    };
    if (productClassification) {
      body.productClassification = productClassification;
    }
    return from(
      this.http.rawPost<{ data: Product }>(this.tourServiceHost, url, body),
    ).pipe(map((d) => d.data));
  }

  setProductType(
    product: Product,
    type: ProductType,
    classification?: string,
  ): Observable<Product> {
    const url = `/operators/products/${product.id}`;
    const body = {
      productType: type,
      productClassification: classification,
    };
    return from(
      this.http.rawPatch<Product>(this.tourServiceHost, url, body),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  getNextStep(product: Product) {
    const lastStep = product.lastFinishStage;
    const lastStepIndex = allStepIdsOrdered.indexOf(lastStep);

    if (lastStep === "done") {
      return "product-status";
    }

    let nextStep = allStepIdsOrdered[0];
    if (lastStepIndex == -1 || lastStepIndex === allStepIdsOrdered.length - 1) {
      nextStep = allStepIdsOrdered[0];
    } else {
      nextStep = allStepIdsOrdered[lastStepIndex + 1];
    }
    return nextStep;
  }

  private isCurrentStageAheadOfTarget(
    product: Product,
    targetStage: string,
  ): boolean {
    if (product.lastFinishStage === "done") {
      return true;
    }
    const currentStageIndex = allStepIdsOrdered.indexOf(
      product.lastFinishStage,
    );
    const targetStageIndex = allStepIdsOrdered.indexOf(targetStage);

    return currentStageIndex > targetStageIndex;
  }

  updateBasicDetails(
    product: Product,
    details: {
      title?: string;
      language?: string;
      subtitle?: string;
      briefDescription?: string;
      description?: string;
      whatToBring?: string;
      goodToKnow?: string;
      isCustomizable?: boolean;
      isPrivate?: boolean;
      completionStage?: string; // for override the default
    },
  ): Observable<Product> {
    const url = `/operators/products/${product.id}`;
    return from(
      this.http.rawPatch<{ data: Product }>(this.tourServiceHost, url, {
        ...details,
        completionStage: details.completionStage || allStepIdsOrdered[0],
      }),
    ).pipe(map((res) => res.data));
  }

  finishMediaStep(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.MEDIA);
  }

  finishMeetingPickup(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.MEETING_PICKUP);
  }

  finishDetailConfig(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.TOUR_ACTIVITY_DETAILS);
  }

  finishFeatureConfig(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.ADDONS);
  }

  finishUniqueConfig(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.SPECIAL);
  }

  finishAdditionInfoConfig(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.ADDITIONAL_INFO);
  }

  finishBasicPricingConfig(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.PRICING_SETUP);
  }

  finishSchedulePricingConfig(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.SCHEDULING);
  }

  finishBookingSettings(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.BOOKING_SETTINGS);
  }

  finishCancellationPolicy(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.CANCELLATION_POLICY);
  }

  private finishStep(product: Product, stepId: string) {
    const isCurrentStageAheadOfTarget = this.isCurrentStageAheadOfTarget(
      product,
      stepId,
    );
    console.log(
      "is current stage ahead of target",
      isCurrentStageAheadOfTarget,
      product.lastFinishStage,
      stepId,
    );
    if (isCurrentStageAheadOfTarget) {
      return of(product);
    }

    const url = `/operators/products/${product.id}/completion-stage`;
    return from(
      this.http.rawPatch<Product>(this.tourServiceHost, url, {
        stage: stepId,
      }),
    );
  }

  manageCategories(
    product: Product,
    categories: ProductCategory[],
  ): Observable<void> {
    return throwError(() => new Error("Not implemented"));
  }

  manageMedia(
    product: Product,
    operations: {
      uploads?: { file: File; isCover?: boolean }[];
      deletions?: ProductMedia[];
      reorder?: ProductMedia[];
    },
  ): Observable<ProductMedia[]> {
    if (operations.uploads?.length) {
      const formData = new FormData();
      operations.uploads.forEach((upload, i) => {
        formData.append("files", upload.file);
        if (upload.isCover) {
          formData.append("isCover", "true");
        }
      });

      const url = `/operators/products/${product.id}/media/upload`;
      return from(
        this.http.rawPost<{ data: ProductMedia[] }>(
          this.tourServiceHost,
          url,
          formData,
          undefined,
          undefined,
          false,
        ),
      ).pipe(map((d: any) => d.data));
    }

    if (operations.deletions?.length) {
      const deletions = operations.deletions.map((media) =>
        this.deleteMediaItem(product, media.id),
      );
      return forkJoin(deletions).pipe(map(() => []));
    }

    if (operations.reorder?.length) {
      const url = `/operators/products/${product.id}/media/reorder`;
      const body = {
        mediaOrder: operations.reorder.map((m) => ({
          id: m.id,
          sortOrder: m.sortOrder,
        })),
      };
      return from(
        this.http.rawPatch<{ data: ProductMedia[] }>(
          this.tourServiceHost,
          url,
          body,
        ),
      ).pipe(map((d) => d.data));
    }

    return of([]);
  }

  updateMediaItem(
    product: Product,
    mediaId: string,
    updates: Partial<ProductMedia>,
  ): Observable<ProductMedia> {
    const url = `/operators/products/${product.id}/media/${mediaId}`;
    return from(
      this.http.rawPatch<ProductMedia>(this.tourServiceHost, url, {
        ...updates,
      }),
    );
  }

  deleteMediaItem(product: Product, mediaId: string): Observable<void> {
    const url = `/operators/products/${product.id}/media/${mediaId}`;
    return from(this.http.rawDelete(this.tourServiceHost, url)).pipe(
      map(() => null),
    );
  }

  configureMeetingPickup(
    product: Product,
    settings: MeetingPickUpSetting,
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/meeting-pickup`;
    const body = {
      startPoints: settings.meetingPoint.startPoints.map(
        ({ instructions, ...point }, index) => ({
          type: "start",
          order: index,
          location: this.mapLocationToDto(point),
          locationInstructions: instructions,
          externalReference: point.externalReference,
        }),
      ),
      endPoints: settings.meetingPoint.endPoints.map(
        ({ instructions, ...point }, index) => ({
          type: "end",
          order: index,
          location: this.mapLocationToDto(point),
          locationInstructions: instructions,
        }),
      ),
      pickupOption: {
        pickupOptionType: settings.pickupOption.type,
        endsAtStartPoint: settings.meetingPoint.endsAtStartPoint,
        additionalInfo: settings.pickupOption.additionalInfo,
      },
      travelerPickup: {
        pickupOptionType: settings.pickupOption.type,
        pickupLocationTypes: settings.pickupPoints?.points.map(
          (p) => p.category,
        ),
        pickupHotels: settings.pickupPoints?.points
          .filter((p) => p.category === PickupPointCategory.HOTEL)
          .map((hotel) => ({
            searchString: hotel.searchString,
            name: hotel.name,
            address: hotel.address,
            centre: {
              lat: hotel.centre.lat,
              long: hotel.centre.long,
            },
            categories: hotel.category ? [hotel.category] : [],
            providerReference: hotel.providerReference,
          })),
        pickupAirports: settings.pickupPoints?.points
          .filter((p) => p.category === PickupPointCategory.AIRPORT)
          .map((ap) => ({
            searchString: ap.searchString,
            name: ap.name,
            address: ap.address,
            centre: {
              lat: ap.centre.lat,
              long: ap.centre.long,
            },
            categories: ap.category ? [ap.category] : [],
            providerReference: ap.providerReference,
          })),
        pickupPorts: settings.pickupPoints?.points
          .filter((p) => p.category === PickupPointCategory.PORT)
          .map((ap) => ({
            searchString: ap.searchString,
            name: ap.name,
            address: ap.address,
            centre: {
              lat: ap.centre.lat,
              long: ap.centre.long,
            },
            categories: ap.category ? [ap.category] : [],
            providerReference: ap.providerReference,
          })),
        // additionalInfo: settings.pickupPoints?.additionalInfo,
        customPickupAllowed: settings.pickupOption?.customPickupAllowed,
        minutesBeforeDepartureTimeForPickup:
          settings.pickupOption?.minutesBeforeDepartureTimeForPickup,
      },
    };

    return from(
      this.http.rawPut<Product>(this.tourServiceHost, url, body),
    ).pipe(
      map((updatedProduct) => {
        return {
          ...product,
          meetingPickupSetting: updatedProduct.meetingPickupSetting,
        };
      }),
    );
  }

  private mapLocationToDto(location: MeetingPoint) {
    return {
      searchString: location.searchString,
      name: location.name,
      address: location.address,
      centre: {
        lat: location.latitude,
        long: location.longitude,
      },
      categories: location.categories ?? [],
      providerReference: location.providerReference,
      serviceProvider: location.provider,
    };
  }

  configureTourDetails(
    product: Product,
    details: {
      durationMinutes: number;
      flexibleDuration?: {
        from: number;
        to: number;
        unit: FlexibleDurationUnit;
      };
      difficultyLevel?: string;
      highlights?: string;
      itineraryStops?: ItineraryStop[];
    },
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/itinerary`;
    const body = {
      durationInMinutes: details.durationMinutes,
      flexibleDuration: details.flexibleDuration,
      highlights: details.highlights,
      difficultyLevel: details.difficultyLevel,
      itineraryItems: details.itineraryStops?.map((stop, index) => ({
        location: this.mapLocationToDto(stop.location),
        description: stop.description,
        durationInMinutes: stop.durationMinutes,
        isPassByWithoutStopping: stop.durationMinutes === 0,
        order: index,
        admissionInclusionType: stop.admissionType,
      })),
    };
    return from(this.http.rawPost<Product>(this.tourServiceHost, url, body));
  }

  configureActivityDetails(
    product: Product,
    details: {
      durationMinutes: number;
      difficultyLevel?: string;
    },
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/itinerary`;
    const body = {
      durationInMinutes: details.durationMinutes,
      difficultyLevel: details.difficultyLevel,
      itineraryItems: [],
    };
    return from(this.http.rawPost<Product>(this.tourServiceHost, url, body));
  }

  manageSchedulePatterns(): Observable<SchedulePattern[]> {
    return throwError(() => new Error("Not implemented"));
  }

  generateSessions(): Observable<ProductSession[]> {
    return throwError(() => new Error("Not implemented"));
  }

  configureBookingSettings(
    product: Product,
    settings: {
      confirmationType: "manual" | "instant";
      bookingCutoffType: "START_TIME" | "FIXED";
      defaultBookingCutoffHours?: number;
      bookingCutoffFixedTime?: string;
      bookingCutoffFixedTimeOnDay?: number;
    },
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/booking-settings`;
    const body = {
      confirmationType: settings.confirmationType,
      bookingCutoffType: settings.bookingCutoffType,
      bookingCutoffMinutes: settings.defaultBookingCutoffHours * 60,
      bookingCutoffFixedTime: settings.bookingCutoffFixedTime,
      bookingCutoffFixedTimeOnDay: settings.bookingCutoffFixedTimeOnDay,
    };
    return from(
      this.http.rawPatch<{ data: Product }>(this.tourServiceHost, url, body),
    ).pipe(map((d) => d.data));
  }

  isAllowUpdatePricingUnit(product: Product): Observable<boolean> {
    const url = `/operators/products/${product.id}/can-change-pricing-unit`;
    return from(
      this.http.rawGet<{ data: { canChange: boolean } }>(
        this.tourServiceHost,
        url,
      ),
    ).pipe(map((d) => d.data.canChange));
  }

  updateBookingSettings(
    product: Product,
    settings: {
      priceUnit: PriceUnit;
      priceUnitGroup?: GroupDefinitionEnum;
      ageBandDefinitions?: AgeDefinition;
      maxParticipants?: number;
    },
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/booking-settings`;
    const body: UpdatePricingSettingsDto = {
      priceUnit: settings.priceUnit,
      groupDefinition: settings.priceUnitGroup,
      maxParticipants: settings.maxParticipants,
      ageBandDefinitions: settings.ageBandDefinitions && {
        ageBands: Object.entries(settings.ageBandDefinitions).map(
          ([ageBand, definition]) => ({
            name: ageBand as AgeBand,
            minAge: definition.minAge,
            maxAge: definition.maxAge,
          }),
        ),
        maxTravelers: settings.maxParticipants,
        childrenAccompanied: true,
      },
    };
    return from(this.http.rawPatch<Product>(this.tourServiceHost, url, body));
  }

  setCancellationPolicy(
    product: Product,
    policy: CancellationPolicy,
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/cancellation-policy`;
    const body = {
      ...policy,
    };
    return from(
      this.http.rawPatch<{ data: Product }>(this.tourServiceHost, url, body),
    ).pipe(map((d) => d.data));
  }

  manageAccessibility(): Observable<Product> {
    return throwError(() => new Error("Not implemented"));
  }

  manageHealthSafety(): Observable<Product> {
    return throwError(() => new Error("Not implemented"));
  }

  saveAdditionalInfo(
    product: Product,
    settings: {
      resellerType: ResellerType;
      contactPhone: string;
      difficultyLevel: string;
      accessibilities: {
        infoType: AdditionalInfoTypeEnum;
        isApplicable: boolean;
        customText?: string;
      }[];
      healthRestrictions: {
        infoType: AdditionalInfoTypeEnum;
        isApplicable: boolean;
        customText?: string;
      }[];
      generalInfos: {
        infoType: AdditionalInfoTypeEnum;
        isApplicable: boolean;
        customText?: string;
      }[];
    },
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/info`;
    const additionalInfo = [
      ...settings.accessibilities.map((a) => ({
        type: a.infoType,
        isApplicable: a.isApplicable,
        customText: a.customText,
      })),
      ...settings.healthRestrictions.map((h) => ({
        type: h.infoType,
        isApplicable: h.isApplicable,
        customText: h.customText,
      })),
      ...settings.generalInfos.map((g) => ({
        type: g.infoType,
        isApplicable: g.isApplicable,
        customText: g.customText,
      })),
    ];

    const body = {
      contactPhone: settings.contactPhone,
      resellerType: settings.resellerType,
      difficultyLevel: settings.difficultyLevel,
      additionalInfo,
    };

    return from(
      this.http.rawPatch<{ data: Product }>(this.tourServiceHost, url, body),
    ).pipe(map((res) => res.data));
  }

  manageFeatures(
    product: Product,
    { inclusions, exclusions },
  ): Observable<ProductFeature[]> {
    const url = `/operators/products/${product.id}/features`;
    const featureToDto = (feature: ProductFeature) => ({
      title: feature.title,
      displayText: feature.title,
      description: feature.description,
      category: feature.category,
    });
    const body = {
      inclusions: inclusions.map(featureToDto),
      exclusions: exclusions.map(featureToDto),
    };
    return from(
      this.http.rawPut<ProductFeature[]>(this.tourServiceHost, url, body),
    );
  }

  manageAddons(): Observable<ProductAddon[]> {
    return throwError(() => new Error("Not implemented"));
  }

  addNewSeason(product: Product, season: ProductSeason): Observable<Product> {
    const url = `/operators/products/${product.id}/seasons`;
    const body = ProductMapper.mapProductSeasonToApiFormat(season);
    return from(
      this.http.rawPost<Product>(this.tourServiceHost, url, body),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  addSeasonToOption(
    product: Product,
    productOptionId: string,
    season: ProductSeason,
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/options/${productOptionId}/seasons`;
    const body = ProductMapper.mapProductSeasonToApiFormat(season);
    return from(
      this.http.rawPost<Product>(this.tourServiceHost, url, body),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  addNewProductOption(
    product: Product,
    productOption: ProductOption,
  ): Observable<Product> {
    const url = `/operators/products/${product.id}/options`;

    const firstSeason = productOption.seasons?.[0];
    const firstPricingRecord = firstSeason?.pricingRecords?.[0];

    let pricingRecord = null;
    if (firstSeason && firstPricingRecord) {
      const apiPricingRecord =
        ProductMapper.mapPricingRecordToApiFormat(firstPricingRecord);

      pricingRecord = apiPricingRecord;
    }

    const body = {
      title: productOption.title,
      isDefault: productOption.isDefault || false,
      isPickupIncluded: productOption.isPickupIncluded,
      externalReference: productOption.id,
      startLocations: productOption.startLocations?.map(
        (location) => location.providerReference || location.searchString,
      ),
      customizations:
        productOption.optionDetals?.map((oD) => ({
          type: oD.type,
          title: oD.name,
          description: oD.description,
          integerValue: oD.integerValue,
          isDefault: false,
        })) || [],
      pricingRecord,
      seasonStartDate: firstSeason
        ? this.formatDateForAPI(firstSeason.startDate)
        : undefined,
      seasonEndDate: firstSeason?.endDate
        ? this.formatDateForAPI(firstSeason.endDate)
        : undefined,
      timeModel: firstSeason?.timeModel,
    };

    return from(
      this.http.rawPost<Product>(this.tourServiceHost, url, body),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  private formatDateForAPI(date: Date): string {
    return date.toISOString().split("T")[0]; // YYYY-MM-DD format
  }

  private convertTimeToAPIFormat(time: string): string {
    const [timeStr, period] = time.split(/(am|pm)/i);
    let [hours, minutes] = timeStr.split(":").map(Number);

    if (period.toLowerCase() === "pm" && hours !== 12) {
      hours += 12;
    } else if (period.toLowerCase() === "am" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${(minutes || 0)
      .toString()
      .padStart(2, "0")}`;
  }

  private convertDayTimeRangesToAPIFormat(
    dayTimeRanges: Record<string, { start: string; end: string }[]>,
  ): Record<string, Array<{ start: string; end: string }>> {
    const converted: Record<string, Array<{ start: string; end: string }>> = {};

    Object.entries(dayTimeRanges).forEach(([day, ranges]) => {
      converted[day] = ranges.map((range) => ({
        start: this.convertTimeToAPIFormat(range.start),
        end: this.convertTimeToAPIFormat(range.end),
      }));
    });

    return converted;
  }

  addPricingRecordToSeason(
    productId: string,
    productOptionId: string,
    productSeason: ProductSeason,
    pricingRecord: PricingRecord,
  ): Observable<Product> {
    const url = `/operators/products/${productId}/options/${productOptionId}/pricing`;

    // Use the mapper to convert UI format to API format
    const apiPricingRecord =
      ProductMapper.mapPricingRecordToApiFormat(pricingRecord);

    const body: any = {
      seasonId: productSeason.id,
      ...apiPricingRecord,
    };

    return from(
      this.http.rawPost<Product>(this.tourServiceHost, url, body),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  submitForReview(productId: string): Observable<Product> {
    const url = `/operators/products/${productId}/submit-for-review`;
    return from(this.http.rawPost<Product>(this.tourServiceHost, url, {})).pipe(
      map((response) => ProductMapper.mapApiProductToModel(response)),
    );
  }

  publish(product: Product): Observable<Product> {
    const url = `/operators/products/${product.id}`;
    const body = { status: "published" };
    return from(
      this.http.rawPatch<Product>(this.tourServiceHost, url, body),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  unpublish(product: Product): Observable<Product> {
    const url = `/operators/products/${product.id}`;
    const body = { status: "unpublished" };
    return from(
      this.http.rawPatch<Product>(this.tourServiceHost, url, body),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  moveToDraft(product: Product): Observable<Product> {
    const url = `/operators/products/${product.id}`;
    const body = { status: "draft" };
    return from(
      this.http.rawPatch<{ data: Product }>(this.tourServiceHost, url, body),
    ).pipe(
      map((response) => ProductMapper.mapApiProductToModel(response.data)),
    );
  }

  archive(product: Product): Observable<Product> {
    const url = `/operators/products/${product.id}`;
    return from(this.http.rawDelete(this.tourServiceHost, url)).pipe(
      mapTo({ ...product, status: ProductStatus.ARCHIVED }),
    );
  }

  getProduct(
    product: { id: string },
    relations?: string[],
  ): Observable<Product> {
    let url = `/operators/products/${product.id}`;
    if (relations && relations.length) {
      url += `?relations=${relations.join(",")}`;
    }
    return from(this.http.rawGet<any>(this.tourServiceHost, url)).pipe(
      map((response) =>
        ProductMapper.mapApiProductToModel(response.data || response),
      ),
    );
  }

  getProductsByOperator(
    operator: { id: string },
    limit: number,
    page: number,
    filters?: {
      status?: ProductStatus;
      type?: ProductType;
      search?: string;
    },
  ): Observable<Product[]> {
    let url = `/operators/products?limit=${limit}&page=${page}&relations=media`;
    if (filters?.status) url += `&status=${filters.status}`;
    if (filters?.type) url += `&type=${filters.type}`;
    if (filters?.search) url += `&search=${encodeURIComponent(filters.search)}`;

    return from(
      this.http.rawGet<{ data: any[] }>(this.tourServiceHost, url),
    ).pipe(
      map((response) =>
        (response.data || []).map(ProductMapper.mapApiProductToModel),
      ),
    );
  }

  getAvailableCancellationPolicies(operator: {
    id: string;
  }): Observable<CancellationPolicy[]> {
    const url = `/operators/${operator.id}/cancellation-policies`;
    return from(
      this.http.rawGet<{ data: CancellationPolicy[] }>(
        this.tourServiceHost,
        url,
      ),
    ).pipe(map((d) => d.data));
  }

  getProductCategories(): Observable<ProductCategory[]> {
    return throwError(() => new Error("Not implemented"));
  }

  createSeasonForOption(
    product: Product,
    productOptionId: string,
    seasonData: ProductSeason,
    pricingRecordData: PricingRecord,
  ): Observable<void> {
    const url = `/operators/products/${product.id}/options/${productOptionId}/seasons`;

    // Use the mapper to convert UI format to API format
    const apiPricingRecord =
      ProductMapper.mapPricingRecordToApiFormat(pricingRecordData);

    const body: any = {
      name: seasonData.name,
      startDate: this.formatDateForAPI(seasonData.startDate),
      endDate: seasonData.endDate
        ? this.formatDateForAPI(seasonData.endDate)
        : undefined,
      timeModel: seasonData.timeModel,
      ...apiPricingRecord,
    };

    return from(
      this.http.rawPost<Product>(this.tourServiceHost, url, body),
    ).pipe(map(() => null));
  }

  updateProductOption(
    product: Product,
    productOption: ProductOption,
  ): Observable<void> {
    const url = `/operators/products/${product.id}/options/${productOption.id}`;

    const body: any = {
      title: productOption.title,
      isPickupIncluded: productOption.isPickupIncluded,
      externalReference: productOption.id,
    };

    if (
      productOption.startLocations &&
      productOption.startLocations.length > 0
    ) {
      body.startLocations = productOption.startLocations.map(
        (location) => location.providerReference || location.searchString,
      );
    }

    if (productOption.optionDetals && productOption.optionDetals.length > 0) {
      body.customizations = productOption.optionDetals.map((detail) => ({
        type: detail.type,
        title: detail.name,
        description: detail.description,
        integerValue: detail.integerValue,
        isDefault: false,
      }));
    }

    return from(
      this.http.rawPatch<Product>(this.tourServiceHost, url, body),
    ).pipe(map(() => null));
  }

  updateSeason(
    productId: string,
    seasonId: string,
    seasonData: Partial<ProductSeason>,
  ): Observable<void> {
    const url = `/operators/products/${productId}/seasons/${seasonId}`;

    const body: any = {};

    if (seasonData.name !== undefined) {
      body.name = seasonData.name;
    }

    if (seasonData.startDate !== undefined) {
      body.startDate = this.formatDateForAPI(seasonData.startDate);
    }

    if (seasonData.endDate !== undefined) {
      body.endDate = seasonData.endDate
        ? this.formatDateForAPI(seasonData.endDate)
        : null;
    }

    if (seasonData.timeModel !== undefined) {
      body.timeModel = seasonData.timeModel;
    }

    return from(
      this.http.rawPatch<Product>(this.tourServiceHost, url, body),
    ).pipe(map(() => null));
  }

  updatePricingRecord(
    productId: string,
    optionId: string,
    recordId: string,
    pricingData: Partial<PricingRecord>,
  ): Observable<void> {
    const url = `/operators/products/${productId}/options/${optionId}/pricing/${recordId}`;

    // Use the mapper to convert UI format to API format
    const apiPricingRecord = ProductMapper.mapPricingRecordToApiFormat(
      pricingData as PricingRecord,
    );

    // Only include defined fields in the update
    const body: any = {};

    Object.keys(apiPricingRecord).forEach((key) => {
      if (apiPricingRecord[key] !== undefined) {
        body[key] = apiPricingRecord[key];
      }
    });

    return from(
      this.http.rawPatch<Product>(this.tourServiceHost, url, body),
    ).pipe(map(() => null));
  }

  deletePricingRecord(
    productId: string,
    optionId: string,
    recordId: string,
  ): Observable<void> {
    const url = `/operators/products/${productId}/options/${optionId}/pricing/${recordId}`;
    return from(
      this.http.rawDelete(this.tourServiceHost, url) as Promise<void>,
    );
  }

  deleteProductOption(productId: string, optionId: string): Observable<void> {
    const url = `/operators/products/${productId}/options/${optionId}`;
    return from(
      this.http.rawDelete(this.tourServiceHost, url) as Promise<void>,
    );
  }

  deleteSeason(productId: string, seasonId: string): Observable<void> {
    const url = `/operators/products/${productId}/seasons/${seasonId}`;
    return from(
      this.http.rawDelete(this.tourServiceHost, url) as Promise<void>,
    );
  }

  getProductsWithOptions(): Observable<
    {
      id: string;
      name: string;
      description?: string;
      options: { id: string; title: string }[];
    }[]
  > {
    const url = `/operators/products/with-options`;
    return from(
      this.http.rawGet(this.tourServiceHost, url).then((d) =>
        (d.data || []).map((product) => ({
          id: product.id,
          name: product.name,
          description: product.description,
          options: (product.options || []).map((o) => ({
            ...o,
          })),
        })),
      ),
    );
  }

  getCommissionRate(): Observable<{ rate: string }> {
    const url = `/operators/commission-rate`;
    return from(
      this.http.rawGet<{ data: { rate: string } }>(this.tourServiceHost, url),
    ).pipe(map((response) => response.data));
  }

  getTaxonomyCategories(facet: string): Observable<any[]> {
    const url = `/operators/products/taxonomy/categories?facet=${facet}`;
    return from(this.http.rawGet(this.tourServiceHost, url) as Promise<any[]>);
  }

  saveTaxonomies(product: Product, categoryIds: string[]): Observable<Product> {
    const url = `/operators/products/${product.id}/taxonomies`;
    const payload = { categoryIds };
    return from(
      this.http.rawPost<Product>(this.tourServiceHost, url, payload),
    ).pipe(map((response) => ProductMapper.mapApiProductToModel(response)));
  }

  finishThemesCategories(product: Product): Observable<Product> {
    return this.finishStep(product, CompletionStageEnum.THEMES_CATEGORIES);
  }

  async updateOperatorProfile(
    profile: UpdateOperatorProfileDto,
  ): Promise<void> {
    const url = `/operators/me`;
    await this.http.rawPatch<void>(
      this.tourServiceHost,
      url,
      profile,
      {},
      false,
    );
  }
}
